import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { Outlet, useLocation, useNavigate, NavLink } from 'react-router-dom'
import { Calendar, ChevronDown, ChevronUp, Clock, Eye, EyeOff, Loader2, MessagesSquare, Settings, ShieldCheck, Wrench, RefreshCw, ClipboardList, FlaskConical, BookOpen, Package } from 'lucide-react'
import { AppShell } from './AppShell.tsx'
import { GlobalStatusBar } from './GlobalStatusBar.tsx'
import { TenantContextBar } from './TenantContextBar.tsx'
import { navCategories, utilityItems } from './MegaMenu.tsx'
import { useAuthStore } from '@/stores/auth.ts'
import { useModulesStore } from '@/stores/modules.ts'
import { useSapPermissionsStore } from '@/stores/sapPermissions.ts'
import { usePresentationStore } from '@/stores/presentation.ts'
import { apiClient } from '@/lib/api.ts'
import { cn } from '@/lib/utils.ts'
import { MODULE_DEFINITIONS } from '@shared/types'

function getModuleForRoute(pathname: string): string | null {
  let best: { id: string; length: number } | null = null
  for (const mod of MODULE_DEFINITIONS) {
    for (const route of mod.routes) {
      if (pathname === route || pathname.startsWith(route + '/')) {
        if (!best || route.length > best.length) {
          best = { id: mod.id, length: route.length }
        }
      }
    }
  }
  return best?.id ?? null
}

function DevDebugPanel() {
  const [debugData, setDebugData] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadDebug = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await apiClient.get('/deals/debug/tags')
      setDebugData(data)
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadDebug()
  }, [loadDebug])

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-3 text-amber-800 text-xs">
        <Loader2 className="w-3 h-3 animate-spin" />
        Debug-Daten werden geladen...
      </div>
    )
  }

  if (error) {
    return <div className="p-3 text-red-700 text-xs">Fehler: {error}</div>
  }

  if (!debugData) return null

  const { vertriebParent, vertriebChildren } = debugData

  return (
    <div className="p-3 text-xs text-amber-900 space-y-2 max-h-[60vh] overflow-y-auto">
      <div className="flex items-center justify-between">
        <span className="font-semibold">TANSS Vertrieb-Tags Debug</span>
        <button onClick={loadDebug} className="text-[10px] underline hover:no-underline">
          Neu laden
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="bg-amber-100 rounded p-2">
          <div className="font-semibold mb-1">Vertrieb Parent-Tag</div>
          {vertriebParent ? (
            <div>ID: {vertriebParent.id}, Name: "{vertriebParent.name}"</div>
          ) : (
            <div className="text-red-600 font-semibold">NICHT GEFUNDEN!</div>
          )}
        </div>

        <div className="bg-amber-100 rounded p-2">
          <div className="font-semibold mb-1">Vertrieb Child-Tags ({vertriebChildren?.length ?? 0})</div>
          {vertriebChildren?.map((t: any) => (
            <div key={t.id}>ID: {t.id} → "{t.name}"</div>
          ))}
        </div>
      </div>

      {debugData.directTicketTest && (
        <div className="bg-amber-100 rounded p-2">
          <div className="font-semibold mb-1">
            Direkttest: Ticket #{debugData.directTicketTest.ticketId}
          </div>
          {debugData.directTicketTest.error ? (
            <div className="text-red-600">
              Fehler: {debugData.directTicketTest.error} (Status: {debugData.directTicketTest.status})
              <pre className="text-[10px] mt-1 whitespace-pre-wrap">{JSON.stringify(debugData.directTicketTest.responseData, null, 2)}</pre>
            </div>
          ) : (
            <div className="space-y-1">
              <div>In /tickets/technician Liste: {debugData.directTicketTest.inTechnicianList ? '✅ JA' : '❌ NEIN'} ({debugData.directTicketTest.techListLength} Tickets)</div>
              <div>Tags (parsed): {debugData.directTicketTest.parsedTags?.length ?? 0}</div>
              <pre className="text-[10px] bg-amber-50 p-1 rounded whitespace-pre-wrap max-h-32 overflow-y-auto">
                {JSON.stringify(debugData.directTicketTest.rawTagAssignmentResponse, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}

      {debugData.ticketTagTest && (
        <div className="bg-amber-100 rounded p-2">
          <div className="font-semibold mb-1">
            Vollscan: {debugData.ticketTagTest.totalTickets} Tickets geprüft
          </div>
          {debugData.ticketTagTest.error ? (
            <div className="text-red-600">Fehler: {debugData.ticketTagTest.error}</div>
          ) : (
            <div className="space-y-1">
              <div>Tickets mit Tags: {debugData.ticketTagTest.totalWithAnyTags}</div>
              <div>Fehler: {debugData.ticketTagTest.totalErrors}</div>
              <div>Alle gefundenen TagIDs: [{debugData.ticketTagTest.allUniqueTagIds?.join(', ')}]</div>
              <div>Vertrieb-Child-IDs: [{debugData.ticketTagTest.vertriebChildIds?.join(', ')}]</div>
              <div className={debugData.ticketTagTest.vertriebMatchCount > 0 ? 'text-green-700 font-bold' : 'text-red-600 font-bold'}>
                Vertrieb-Treffer: {debugData.ticketTagTest.vertriebMatchCount}
              </div>
              {debugData.ticketTagTest.vertriebMatches?.map((m: any) => (
                <div key={m.ticketId} className="text-green-700">
                  #{m.ticketId} "{m.title}" [tagIds: {m.tags?.join(', ')}]
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="text-[10px] text-amber-700">
        Gesamt-Tags in TANSS: {debugData.count}
      </div>
    </div>
  )
}

function HeaderClock() {
  const [time, setTime] = useState(new Date())
  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000)
    return () => clearInterval(interval)
  }, [])
  return (
    <div className="hidden md:flex items-center gap-1.5 text-sm font-mono text-white/70">
      <Clock className="w-3.5 h-3.5" />
      {time.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
    </div>
  )
}

function DemoModeToggle() {
  const anonymize = usePresentationStore((s) => s.anonymize)
  const toggleAnonymize = usePresentationStore((s) => s.toggleAnonymize)
  return (
    <button
      onClick={toggleAnonymize}
      title={
        anonymize
          ? 'Präsentations-Modus aktiv – Daten sind anonymisiert. Klicken zum Deaktivieren.'
          : 'Präsentations-Modus aktivieren – Namen, Beträge, IBANs etc. werden anonymisiert'
      }
      className={cn(
        'flex items-center gap-1.5 px-2 py-1.5 rounded text-sm transition-colors',
        anonymize
          ? 'bg-accent-500 text-white hover:bg-accent-600'
          : 'text-white/70 hover:bg-white/10 hover:text-white',
      )}
    >
      {anonymize ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      <span className="hidden lg:inline text-xs font-medium">
        {anonymize ? 'Demo-Modus' : 'Demo'}
      </span>
    </button>
  )
}

function SettingsButton() {
  const navigate = useNavigate()
  return (
    <button
      onClick={() => navigate('/einstellungen')}
      className="p-1.5 rounded hover:bg-white/10 text-white/70 hover:text-white transition-colors"
      aria-label="Einstellungen"
    >
      <Settings className="w-4 h-4" />
    </button>
  )
}

const SYSTEM_MENU_SECTIONS = [
  {
    label: 'Integrationen',
    items: [
      { to: '/admin/tanss-teams-bridge', icon: MessagesSquare, label: 'TANSS↔Teams Bridge', description: 'Sync-Status & Mapping' },
    ],
  },
  {
    label: 'Sync & Kalender',
    items: [
      { to: '/debug?tab=sync-ops', icon: RefreshCw, label: 'Sync-Vorgänge', description: 'Alle Sync-Jobs auflisten & ausführen' },
      { to: '/debug?tab=sync-map', icon: ClipboardList, label: 'Sync-Map', description: 'Outlook↔TANSS Kalender-Verknüpfungen' },
    ],
  },
  {
    label: 'Diagnose',
    items: [
      { to: '/debug?tab=adapters', icon: FlaskConical, label: 'Adapter-Tests', description: 'Verbindung zu externen Systemen prüfen' },
      { to: '/debug?tab=self-test', icon: ShieldCheck, label: 'Self-Test', description: 'M365-Token, TANSS-Login & Co.' },
      { to: '/debug/kalender-diagnose', icon: Calendar, label: 'Kalender-Sync Diagnose', description: 'User-Trennung Outlook↔TANSS prüfen' },
      { to: '/debug?tab=tanss', icon: Wrench, label: 'TANSS-API-Viewer', description: 'Raw-API-Abfragen gegen TANSS' },
      { to: '/debug?tab=egis', icon: Package, label: 'EGIS-Debug', description: 'XML-Direkttest gegen EGIS Business Connector' },
    ],
  },
  {
    label: 'Protokolle',
    items: [
      { to: '/debug?tab=audit', icon: ShieldCheck, label: 'Audit-Logs', description: 'Alle Aktionen aller Nutzer' },
      { to: '/debug?tab=job-history', icon: BookOpen, label: 'Job-History', description: 'Verlauf aller Cron-Läufe' },
    ],
  },
]

function SystemMenuButton() {
  const allowed = useModulesStore((s) => s.isAllowed('dev-tools'))
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const location = useLocation()

  useEffect(() => { setOpen(false) }, [location.pathname, location.search])

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  if (!allowed) return null

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'p-1.5 rounded transition-colors',
          open ? 'bg-white/15 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white',
        )}
        aria-label="Debug"
        title="Debug — Integrationen, Diagnose, Sync, Protokolle"
      >
        <Wrench className="w-4 h-4" />
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 w-72 z-[100]">
          <div className="p-1.5">
            <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Debug
            </div>
            {SYSTEM_MENU_SECTIONS.map((section) => (
              <div key={section.label} className="mb-1">
                <div className="px-3 py-1 text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                  {section.label}
                </div>
                {section.items.map((item) => {
                  const Icon = item.icon
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-3 px-3 py-2 rounded transition-colors group',
                          isActive ? 'bg-brand-50 text-brand-700' : 'text-gray-700 hover:bg-gray-50',
                        )
                      }
                    >
                      <Icon className="w-4 h-4 flex-shrink-0 text-brand-500 group-hover:text-brand-600" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium leading-tight">{item.label}</div>
                        <div className="text-xs text-gray-500 leading-tight truncate">{item.description}</div>
                      </div>
                    </NavLink>
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}


export function Layout() {
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const fetchStatuses = useModulesStore((s) => s.fetchStatuses)
  const loaded = useModulesStore((s) => s.loaded)
  const fetchAccess = useModulesStore((s) => s.fetchAccess)
  const accessLoaded = useModulesStore((s) => s.accessLoaded)
  const isAdmin = useModulesStore((s) => s.isAdmin)
  const accessOverrides = useModulesStore((s) => s.accessOverrides)
  const fetchSapPermissions = useSapPermissionsStore((s) => s.fetchPermissions)
  const [devModeModules, setDevModeModules] = useState<string[]>([])
  const [debugOpen, setDebugOpen] = useState(false)
  const location = useLocation()

  const currentModuleId = useMemo(() => getModuleForRoute(location.pathname), [location.pathname])
  const devMode = currentModuleId !== null && devModeModules.includes(currentModuleId)

  useEffect(() => {
    if (!loaded) fetchStatuses()
    if (!accessLoaded) fetchAccess()
    fetchSapPermissions()
    apiClient
      .get('/auth/dev-mode')
      .then(({ data }) => {
        setDevModeModules(Array.isArray(data.modules) ? data.modules : [])
      })
      .catch(() => {})
  }, [loaded, fetchStatuses, accessLoaded, fetchAccess, fetchSapPermissions, location.pathname])

  const filteredNav = useMemo(
    () =>
      navCategories
        .map((cat) => ({
          ...cat,
          items: cat.items.filter(
            (item) => !item.moduleId || isAdmin || accessOverrides[item.moduleId] === true,
          ),
        }))
        .filter((cat) => cat.items.length > 0),
    [isAdmin, accessOverrides],
  )

  const topBar = (
    <>
      <div
        className={cn(
          'flex-shrink-0 px-4 py-1 flex items-center text-xs relative z-[60]',
          devMode ? 'bg-amber-400 text-amber-900' : 'bg-brand-600 text-white/70',
        )}
      >
        <div id="footer-portal" className="flex items-center gap-3 flex-1 min-w-0" />
        {devMode && (
          <button
            onClick={() => setDebugOpen(!debugOpen)}
            className="flex items-center justify-center font-semibold cursor-pointer hover:bg-amber-500/30 transition-colors px-2 py-0.5 rounded"
            title={`Dev-Mode aktiv für Modul: ${currentModuleId}`}
          >
            Dev-Mode aktiv — Modul „{currentModuleId}" nutzt Dev-Instanzen
            {debugOpen ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
          </button>
        )}
        <div className="ml-3 z-[60]">
          <GlobalStatusBar />
        </div>
        <span
          className={cn(
            'text-[10px] font-mono flex-shrink-0 ml-auto relative z-10',
            devMode ? 'text-amber-800' : 'text-white/50',
          )}
        >
          v{__BUILD_VERSION__} ·{' '}
          <a
            href={`https://github.com/nilsrochholl/SystemhausManager/commit/${__BUILD_FULL_COMMIT__}`}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'underline decoration-dotted hover:decoration-solid',
              devMode ? 'text-amber-800 hover:text-amber-950' : 'text-white/60 hover:text-white/90',
            )}
          >
            {__BUILD_COMMIT__}
          </a>
          {__BUILD_PR_NUMBER__ && (
            <>
              {' '}
              <a
                href={`https://github.com/nilsrochholl/SystemhausManager/pull/${__BUILD_PR_NUMBER__}`}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  'underline decoration-dotted hover:decoration-solid',
                  devMode
                    ? 'text-amber-800 hover:text-amber-950'
                    : 'text-white/60 hover:text-white/90',
                )}
              >
                PR#{__BUILD_PR_NUMBER__}
              </a>
            </>
          )}
          {' · '}
          <a
            href={`https://github.com/nilsrochholl/SystemhausManager/compare/${__BUILD_FULL_COMMIT__}...main`}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'underline decoration-dotted hover:decoration-solid',
              devMode ? 'text-amber-800 hover:text-amber-950' : 'text-white/60 hover:text-white/90',
            )}
            title="Klicken um zu sehen ob neuere Commits verfügbar sind"
          >
            {__BUILD_DATE__.slice(0, 16).replace('T', ' ')}
          </a>
        </span>
      </div>
      {devMode && debugOpen && (
        <div className="flex-shrink-0 bg-amber-200 border-b border-amber-400">
          <DevDebugPanel />
        </div>
      )}
    </>
  )

  const headerExtras = (
    <>
      <HeaderClock />
      <DemoModeToggle />
      <SystemMenuButton />
      <SettingsButton />
    </>
  )

  return (
    <AppShell
      brand={{
        homeTo: '/dashboard',
        title: 'implec',
        subtitle: 'Systemhaus-Manager',
      }}
      nav={filteredNav}
      utilityItems={utilityItems}
      user={user}
      onLogout={logout}
      headerExtras={headerExtras}
      topBar={topBar}
      contextBar={<TenantContextBar />}
    >
      <Outlet />
    </AppShell>
  )
}
